<template>
  <div class="multi-upload-container">
    <input
      type="file"
      accept=".png,.jpg,.jpeg,.pdf,.docx"
      multiple
      @change="handleFiles"
      class="hidden-file-input"
      ref="fileInput"
    />
    <div class="mx-5 upload-area" @click="$refs.fileInput.click()">
      <img src="@/assets/images/icons/upload-icon.svg" alt="Upload Icon" />
      <p>
        {{ files.length ? "Click to add more files" : "Click to upload files" }}
      </p>
    </div>

    <div class="file-preview-list mx-4" v-if="files.length">
      <div v-for="(file, index) in files" :key="index" class="file-preview">
        <img
          v-if="file.preview && file.isImage"
          :src="file.preview"
          class="preview-img"
        />
        <img v-else :src="file.icon" class="preview-img" />
        <span class="file-name">{{ file.name }}</span>
        <div class="preview-actions">
          <feather-icon
            v-if="file.existing && file.isImage"
            title="Preview Image"
            icon="EyeIcon"
            @click.stop="viewImagePopup(file)"
            class="eye-icon mr-2 text-success"
            svgClasses="w-5 h-5 hover:text-primary stroke-current cursor"
          />
          <feather-icon
            v-show="file.existing"
            title="Download Uploaded File"
            icon="DownloadIcon"
            @click.stop="downloadFile(file)"
            class="download-icon icon_row text-warning"
            svgClasses="w-5 h-5 hover:text-primary stroke-current cursor"
          />
        </div>
        <button
          v-if="!file.existing"
          @click="removeFile(index)"
          class="remove-btn"
        >
          ✕
        </button>
      </div>
      <div class="centerx">
        <vs-popup
          classContent="popup-example"
          :title="$t('View Image')"
          v-if="previewUrl"
          :active.sync="showPreview"
          class="popup-close"
        >
          <div>
            <img
              :src="previewUrl"
              alt="Image Preview"
              class="w-full h-auto object-contain"
            />
          </div>
        </vs-popup>
      </div>
    </div>
  </div>
</template>

<script>

export default {
  name: "MultiFileUploader",
  data() {
    return {
      files: [],
      showPreview: false,
      previewUrl: "",
    };
  },
  props: {
    existingFiles: {
      type: Array,
      default: () => [],
    },
  },
  methods: {
    async downloadFile(file) {
      this.$emit("downloadDocument", file.name, file.id);
    },
    viewImagePopup(file) {
      this.previewUrl = file.url;
      this.showPreview = true;
    },
    handleFiles(event) {
      const allowedTypes = [
        "image/png",
        "image/jpeg",
        "application/pdf",
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      ];
      const newFiles = Array.from(event.target.files);
      newFiles.forEach((file) => {
        if (!allowedTypes.includes(file.type)) {
          this.$toasted.error(
            "Unsupported file type. Only PNG, JPG, JPEG, PDF, and DOCX are allowed.",
            {
              duration: 4000,
            }
          );
          return;
        }
        const isImage = file.type.startsWith("image/");
        const fileData = {
          id: file.id,
          name: file.name,
          file: file,
          preview: null,
          isImage,
          icon: isImage ? null : this.getFileIconFromType(file.type),
          existing: false,
        };

        if (isImage) {
          const reader = new FileReader();
          reader.onload = (e) => {
            fileData.preview = e.target.result;
            this.files.push(fileData);
            this.emitFiles();
          };
          reader.readAsDataURL(file);
        } else {
          this.files.push(fileData);
          this.emitFiles();
        }
      });

      event.target.value = null;
    },
    removeFile(index) {
      this.files.splice(index, 1);
      this.emitFiles();
    },
    emitFiles() {
      // Emit only new files to be uploaded
      const rawFiles = this.files.filter((f) => !f.existing).map((f) => f.file);
      this.$emit("filesUpdate", rawFiles);
    },
    loadExistingFiles() {
      this.files = [];
      if (this.existingFiles && this.existingFiles.length > 0) {
        this.existingFiles.forEach((f) => {
          const isImage = f.type && f.type.startsWith("image/");
          const fileData = {
            id: f.id,
            name: f.fileName,
            preview: isImage ? f.imageUrl : null,
            isImage: isImage,
            icon: isImage ? null : this.getFileIconFromType(f.type),
            file: null,
            existing: true,
            url: isImage ? f.imageUrl : null,
          };
          this.files.push(fileData);
        });
      }
    },
    getFileIconFromType(type) {
      if (type === "application/pdf") {
        return require("@/assets/images/icons/pdf.png");
      }
      if (
        type === "application/msword" ||
        type ===
          "application/vnd.openxmlformats-officedocument.wordprocessingml.document"
      ) {
        return require("@/assets/images/icons/docx.png");
      }
      if (
        type === "application/vnd.ms-excel" ||
        type ===
          "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
      ) {
        return require("@/assets/images/icons/excel.png");
      }
      return require("@/assets/images/icons/upload-icon.svg");
    },
  },
  watch: {
    existingFiles(newVal) {
      if (newVal) {
        this.loadExistingFiles(newVal);
      }
    },
  },
  mounted() {
    this.loadExistingFiles();
  },
};
</script>

<style scoped>
.multi-upload-container {
  width: 100%;
}

.upload-area {
  border: 2px dashed #bbb5ff;
  height: 60px;
  border-radius: 10px;
  background: #f3f3ff;
  cursor: pointer;
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;
}

.upload-area img {
  width: 30px;
  margin-bottom: 10px;
}

.file-preview-list {
  margin-top: 10px;
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}

.file-preview {
  position: relative;
  border: 1px solid #ccc;
  padding: 10px;
  border-radius: 8px;
  background: white;
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100px;
}

.preview-img {
  width: 40px;
  height: 40px;
  object-fit: contain;
}

.file-name {
  font-size: 0.75rem;
  margin-top: 5px;
  text-align: center;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 100%;
  /* Ensures it doesn't exceed parent width */
  display: block;
  /* Ensures it respects box model for width */
}

.remove-btn {
  position: absolute;
  top: 2px;
  right: 4px;
  background: #ff4d4d;
  border: none;
  color: white;
  border-radius: 50%;
  cursor: pointer;
  width: 18px;
  height: 18px;
  font-size: 12px;
  line-height: 1;
}

.hidden-file-input {
  display: none;
}

.preview-actions {
  display: flex;
  gap: 6px;
  margin-top: 5px;
}
</style>
